import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import Masonry from 'react-masonry-component';
import { useQuery, useMutation } from '@apollo/react-hooks';
import moment from 'moment-timezone';
import DateTimePicker from 'react-datetime-picker';
import toast from 'react-hot-toast';
import colors from '../../styles/colors';
import TemplatesSection from '../templates/TemplatesSection';
import Button from '../Button';
import UpcomingRevenueCard from '../UpcomingRevenueCard';
import LifeTimeRevenueCard from '../LifeTimeRevenueCard';
import TotalClientsCard from '../TotalClientsCard';
import TotalCouponsRedeemedCard from '../TotalCouponsRedeemedCard';
import TotalCreditRedeemedCard from '../TotalCreditRedeemedCard';
import TotalCreditActivatedCard from '../TotalCreditActivatedCard';
import TotalCouponsActivatedCard from '../TotalCouponsActivatedCard';
import {
  GET_SUPERADMIN_METRICS,
  GET_ALL_ORGANIZATIONS,
  SET_ORG_CONTEXT_ID,
  SET_MMS_ENABLED,
  RESET_ORG,
  GET_ENV_DATA,
} from '../../graphql/queries';
import Container from '../../shared/Container';
import * as Price from '../../util/Price';
import * as Time from '../../util/Time';
import * as Phone from '../../util/PhoneNumber';
import Section, {
  SectionContainer,
  SectionHeader,
  HeaderText,
  Toolbar,
  SectionContent,
} from '../../shared/Section';
import BaseModal, { ModalList } from '../modals/BaseModal';
import { useMobileMedia } from '../../util/MediaQuery';
import ClientsList from './mobile_ui/ClientsList';
import Loader from '../Loader';
import Icon from '../Icon';
import { IconEnum as Icons } from '../Icons';

const ToolbarItemText = styled.p`
  margin: ${(props) => props.margin || '0'};
`;

const ToolBarItem = styled.form`
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.2s;
  font-weight: ${(props) => (props.active ? '600' : '500')};
  color: ${(props) => (props.active ? colors.seaGreen : colors.grey3)};
  margin-right: 15px;
  font-size: 1.4rem;
  cursor: pointer;
  width: max-content;
  border-radius: 10px;
  padding: 5px 10px;
  background: ${(props) => (props.active ? 'rgba(26, 188, 156, 0.1)' : null)};
  @media screen and (max-width: 991px) {
    ${(props) => props.headerDirection === 'column' && 'margin-top:10px'};
  }
  @media screen and (max-width: 767px) {
    ${(props) => props.headerDirection === 'column' && 'margin-top:10px'};
    flex-direction: column;
    width: ${(props) => (props.isCustom ? '100%' : null)};
    flex-wrap: wrap;
  }
`;
const GlobalStyle = createGlobalStyle`
.react-calendar{
  color:black;
  font-weight:500;
  font-size: 13px !important;
  font-family:'Poppins', sans-serif !important;
}
.react-datetime-picker__inputGroup__leadingZero{
  display: inline-flex;
  align-items: center;
 }
  .react-datetime-picker__wrapper{
   display:inline-flex;
 }
  .react-datetime-picker__inputGroup__input {
    color: ${(props) => (props.active ? colors.seaGreen : colors.grey3)};
  }
  .react-datetime-picker__wrapper{
    border-radius:8px;
    padding:0 5px;
    @media screen and (max-width:450px){
      width: 170px;
      display: flex;
    }
  }
  .react-datetime-picker__button svg {
   stroke: grey  ;
  }
  .react-datetime-picker__clear-button{
   display:none 
  }
  .react-datetime-picker__inputGroup{
   
    @media screen and (max-width:450px){
      display: flex;
    justify-content:center;
    align-items: center;
    }
   
  }
  .react-calendar__tile--hasActive{
    background: #f0f0f0 !important;
  }
`;

const TableContainer = styled.div`
  background: ${colors.white};
  width: 100%;
  height: auto;
  overflow: auto;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  border-radius: 0px 10px 10px 0px;
  border: 1px solid ${colors.grey6};
  position: relative;

  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  &::-webkit-scrollbar-track {
    box-shadow: inset 0 0 5px grey;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: grey;
    border-radius: 10px;
  }

  @media screen and (max-width: 991px) {
    width: 100%;
  }
`;

const TableRow = styled.div`
  transition: all 0.1s;
  align-items: center;
  display: table;
  cursor: pointer;
  min-height: 40px;
  &:hover {
    background: rgba(211, 211, 211, 0.1);
  }
`;

const TableHeader = styled.div`
  transition: all 0.1s;
  align-items: center;
  display: table;
  min-height: 40px;
  &:hover {
    background: rgba(211, 211, 211, 0.1);
  }
`;

const TableRowItem = styled.div`
  color: ${(props) => (props.header ? colors.grey3 : colors.grey1)};
  border-bottom: 1px solid ${colors.grey6};
  border-right: 1px solid ${colors.grey6};
  font-size: 1.4rem;
  width: ${(props) => props.minWidth || '140px'};
  min-width: ${(props) => props.minWidth || '140px'};
  background: ${colors.white};
  height: 100%;
  display: table-cell;
  word-break: break-word;
  padding: 10px 15px;
  &:hover {
    cursor: pointer;
  }
  &:first-child {
    position: sticky;
    top: 0;
    left: 0;
    z-index: 50;
    justify-content: space-between;
  }
`;

const ActiveSteps = Object.freeze({
  AllTime: 'AllTime',
  Today: 'Today',
  PastWeek: '1W',
  PastMonth: '1M',
  MonthToDate: 'MTD',
  YearToDate: 'YTD',
  PastYear: '1Y',
  DateRange: 'Custom',
});

const TableHeaderComponent = ({ ordering, ...props }) => {
  const { fieldName, order } = ordering;
  let orderingIcon = (
    <Icon
      icon={Icons.Sort}
      size={12}
      margin="5px 0 0 0"
      active
      activeColor={colors.grey3}
    />
  );

  if (fieldName === props.columnName && order === 'asc') {
    orderingIcon = (
      <Icon
        margin="5px 0 0 0"
        icon={Icons.SortUp}
        size={12}
        active
        activeColor={colors.grey3}
      />
    );
  } else if (fieldName === props.columnName && order === 'desc') {
    orderingIcon = (
      <Icon
        margin="5px 0 0 0"
        icon={Icons.SortDown}
        size={12}
        active
        activeColor={colors.grey3}
      />
    );
  }

  return (
    <TableRowItem
      header
      minWidth={props.minWidth || '180px'}
      onClick={() => props.onClick(props.columnName) || null}
    >
      {props.children} {props?.onClick ? orderingIcon : null}
    </TableRowItem>
  );
};

export default function SuperAdmin() {
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(0);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [showCustomRange, setShowCustomRange] = useState(false);
  const isMobile = useMobileMedia();
  const [clientData, setClientData] = useState({});
  const [mmsEnabledModal, setMmsEnabledModal] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [clientDetailsModalVisible, setClientDetailsModalVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(ActiveSteps.AllTime);
  const [mmsActivatedIds, setMmsActivatedIds] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [ordering, setOrdering] = useState({ fieldName: 'name', order: 'asc' });
  const [resetOrgModal, setResetOrgModal] = useState(false);
  const [isStagingEnv, setIsStagingEnv] = useState(false);
  const { data, loading } = useQuery(GET_SUPERADMIN_METRICS, {
    fetchPolicy: 'network-only',
    variables: {
      start,
      end,
    },
  });
  useQuery(GET_ENV_DATA, {
    fetchPolicy: 'network-only',
    onCompleted(reponse) {
      if (reponse?.getEnvData?.nodeEnv === 'staging') {
        setIsStagingEnv(true);
      }
    },
  });
  const {
    data: orgData,
    loading: orgLoading,
    refetch,
  } = useQuery(GET_ALL_ORGANIZATIONS, {
    fetchPolicy: 'network-only',
      variables: {
        start,
        end,
  },
    onCompleted(data) {
      setAllClients([]);
      setAllClients(data?.getAllOrganizations);
    },
  });

  const [setOrganizationContextId] = useMutation(SET_ORG_CONTEXT_ID, {
    onError(error) {
      console.log(error);
    },
    onCompleted(data) {
      localStorage.setItem(
        'CHECK_THIS_OUT_AUTH_TOKEN',
        data.setOrganizationContextId.token,
      );
      window.location.replace('/admin/redeemCoupon');
    },
  });

  const [sendMmsEnabled, { loading: mmsLoading }] = useMutation(
    SET_MMS_ENABLED,
    {
      variables: {
        organizationId,
        mmsEnabled: 'Y',
      },
      onError(error) {
        console.log(error);
      },
      onCompleted() {
        setAllClients([]);
        refetch();
        setMmsActivatedIds((old) => [...old, organizationId]);
        setMmsEnabledModal(false);
      },
    },
  );

  const [resetOrganization, { loading: resetOrgLoading }] = useMutation(
    RESET_ORG,
    {
      variables: {
        organizationId,
      },
      onError(error) {
        console.log(error);
      },
      onCompleted(data) {
        const updatedData = data?.resetOrganization;
        if (updatedData) {
          const allClientsUpdated = allClients.map((item) => (item._id === updatedData._id ? updatedData : item));
          setAllClients(allClientsUpdated);
        }
        toast.success('Organization has been reset successfully');
        setResetOrgModal(false);
      },
    },
  );

  useEffect(() => {
    if (!orgLoading) {
      setAllClients(orgData?.getAllOrganizations || []);
    }
  }, [orgLoading]); // eslint-disable-line

  useEffect(() => {
    const filterData = sessionStorage.filterData
      ? JSON.parse(sessionStorage.filterData)
      : null;

    if (filterData) {
      const isCustom = filterData.stepName === ActiveSteps.DateRange;
      setActiveStep(filterData.stepName);
      setStart(filterData.startDate);
      setEnd(filterData.endDate);
      setStartDate(() => {
        const updatedDate = isCustom
          ? new Date(filterData.startDate * 1000)
          : new Date();
        const dateWithStartTime = moment(updatedDate).startOf('day');
        return dateWithStartTime._d;
      });
      setEndDate(() => {
        const updatedDate = isCustom
          ? new Date(filterData.endDate * 1000)
          : new Date();
        const dateWithEndTime = moment(updatedDate).endOf('day');
        return dateWithEndTime._d;
      });
      setShowCustomRange(isCustom);
    }
  }, []);

  const dashboradToolbarItems = [
    {
      text: 'All Time',
      active: activeStep === ActiveSteps.AllTime,
      onClick: () => {
        setActiveStep(ActiveSteps.AllTime);
        setStart(0);
        setEnd(0);
      },
    },
    {
      text: 'Today',
      active: activeStep === ActiveSteps.Today,
      onClick: () => {
        setActiveStep(ActiveSteps.Today);
        setStart(moment().startOf('day').unix());
        setEnd(moment().endOf('day').unix());
      },
    },
    {
      text: '1W',
      active: activeStep === ActiveSteps.PastWeek,
      onClick: () => {
        setActiveStep(ActiveSteps.PastWeek);
        setStart(moment().endOf('day').unix() - Time.DAY * 7);
        setEnd(moment().endOf('day').unix());
      },
    },
    {
      text: '1M',
      active: activeStep === ActiveSteps.PastMonth,
      onClick: () => {
        setActiveStep(ActiveSteps.PastMonth);
        setStart(moment().endOf('day').unix() - Time.DAY * 30);
        setEnd(moment().endOf('day').unix());
      },
    },
    {
      text: 'MTD',
      active: activeStep === ActiveSteps.MonthToDate,
      onClick: () => {
        setActiveStep(ActiveSteps.MonthToDate);
        setStart(moment().startOf('month').unix());
        setEnd(moment().endOf('day').unix());
      },
    },
    {
      text: 'YTD',
      active: activeStep === ActiveSteps.YearToDate,
      onClick: () => {
        setActiveStep(ActiveSteps.YearToDate);
        setStart(moment().startOf('year').unix());
        setEnd(moment().endOf('day').unix());
      },
    },
    {
      text: '1Y',
      active: activeStep === ActiveSteps.PastYear,
      onClick: () => {
        setActiveStep(ActiveSteps.PastYear);
        setStart(moment().endOf('day').unix() - Time.DAY * 365);
        setEnd(moment().endOf('day').unix());
      },
    },
    {
      text: 'Custom',
      active: activeStep === ActiveSteps.DateRange,
      onClick: () => {
        setShowCustomRange(true);
      },
    },
  ];
  const filterByDateRange = (e) => {
    e.preventDefault();
    if (startDate && endDate) {
      setActiveStep(ActiveSteps.DateRange);
      setStart(moment(startDate).unix());
      setEnd(moment(endDate).unix());
    }
  };

  useEffect(() => {
    const setFilterLocalData = () => {
      sessionStorage.filterData = JSON.stringify({
        stepName: activeStep,
        startDate: start,
        endDate: end,
      });
    };
    setFilterLocalData();
    if (activeStep !== ActiveSteps.DateRange) {
      return setShowCustomRange(false);
    }
    setShowCustomRange(true);
  }, [activeStep, end, start]);

  // Perform sorting client side
  const dynamicsort = (property, order) => {
    let sort_order = 1;
    if (order === 'desc') {
      sort_order = -1;
    }
    return function (a, b) {
      // a should come before b in the sorted order
      if (a[property] < b[property]) {
        return -1 * sort_order;
        // a should come after b in the sorted order
      }
      if (a[property] > b[property]) {
        return 1 * sort_order;
        // a and b are the same
      }
      return 0 * sort_order;
    };
  };

  const handleSort = (columnName) => {
    const newOrderDirection = ordering.order === 'asc' ? 'desc' : 'asc';
    const shortedClients = allClients.sort(
      dynamicsort(columnName, newOrderDirection),
    );
    setOrdering({ fieldName: columnName, order: newOrderDirection });
    setAllClients(shortedClients);
  };

  return (
    <>
      <BaseModal
        open={clientDetailsModalVisible}
        onClose={setClientDetailsModalVisible}
        modalType={ModalList.ClientDetailsModal}
        clientData={clientData}
      />

      <BaseModal
        open={mmsEnabledModal}
        onClose={setMmsEnabledModal}
        modalType={ModalList.SetMmsEnabledModal}
        noDelete
        header={`Are you sure you want to set ${orgName} to MMS Enabled?`}
        onConfirm={sendMmsEnabled}
        loading={mmsLoading}
      />
      {isStagingEnv && (
        <BaseModal
          open={resetOrgModal}
          onClose={setResetOrgModal}
          modalType={ModalList.ResetOrgModal}
          header={`Are you sure you want to clear all data of ${orgName}?`}
          onConfirm={resetOrganization}
          loading={resetOrgLoading}
        />
      )}

      <Container>
        <SectionContainer>
          <SectionHeader headerDirection="column">
            <HeaderText>Superadmin</HeaderText>
            <Toolbar>
              <GlobalStyle active={activeStep === ActiveSteps.DateRange} />
              <>
                {dashboradToolbarItems.map((item, index) => {
                  return (
                    <ToolBarItem
                      active={item.active}
                      onClick={() => item.onClick()}
                      onSubmit={(e) => filterByDateRange(e)}
                      key={index}
                      headerDirection="column"
                      isCustom={item.text === 'Custom' && showCustomRange}
                    >
                      <ToolbarItemText
                        margin={
                          isMobile && showCustomRange && item.text === 'Custom'
                            ? '0 0 10px 0'
                            : (!isMobile && showCustomRange && item.text)
                              === 'Custom'
                              ? '0 10px 0 0'
                              : '0'
                        }
                      >
                        {item.text}
                      </ToolbarItemText>
                      {showCustomRange && item.text === ActiveSteps.DateRange && (
                        <>
                          <DateTimePicker
                            clearIcon={<></>}
                            format="MM-dd-y"
                            disableClock
                            minDate={new Date('2000-01-01')}
                            maxDate={endDate}
                            value={startDate}
                            onChange={(val) => setStartDate(val)}
                            required
                          />
                          <h3
                            style={{
                              margin: '0px 5px',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            -
                          </h3>
                          <DateTimePicker
                            format="MM-dd-y"
                            clearIcon={<></>}
                            disableClock
                            minDate={startDate}
                            maxDate={new Date()}
                            value={endDate}
                            onChange={(val) => setEndDate(val)}
                            required
                          />
                          <Button
                            type="submit"
                            width="60px"
                            height="30px"
                            margin={isMobile ? '10px 7px' : '0 7px'}
                            text="Apply"
                          />
                        </>
                      )}
                    </ToolBarItem>
                  );
                })}
              </>
            </Toolbar>
          </SectionHeader>

          <SectionContent loading={loading ? '1' : '0'}>
            {loading ? (
              <Loader />
            ) : (
              (() => {
                const { getSuperAdminMetrics: metrics } = data;
                return (
                  <div style={{ width: '100%' }}>
                    <Masonry options={{ horizontalOrder: true }}>
                      <UpcomingRevenueCard metrics={metrics} />
                      <LifeTimeRevenueCard metrics={metrics} />
                      <TotalClientsCard metrics={metrics} />
                      <TotalCouponsRedeemedCard
                        timeRange={{ start, end }}
                        metrics={metrics}
                      />
                      <TotalCreditRedeemedCard
                        timeRange={{ start, end }}
                        metrics={metrics}
                      />
                      <TotalCreditActivatedCard
                        timeRange={{ start, end }}
                        metrics={metrics}
                      />
                      <TotalCouponsActivatedCard
                        timeRange={{ start, end }}
                        metrics={metrics}
                      />
                    </Masonry>
                  </div>
                );
              })()
            )}
          </SectionContent>
        </SectionContainer>
        <Section headerTitle="Clients" loading={orgLoading}>
          {isMobile ? (
            <ClientsList
              setClientData={setClientData}
              clientData={clientData}
              clients={orgData?.getAllOrganizations}
              setMmsEnabledModal={setMmsEnabledModal}
              setOrgName={setOrgName}
              refetch={refetch}
              isStagingEnv={isStagingEnv}
            />
          ) : (
            <TableContainer>
              <TableHeader>
                <TableHeaderComponent
                  ordering={ordering}
                  columnName="name"
                  minWidth="210px"
                  onClick={handleSort}
                >
                  ORGANIZATION NAME
                </TableHeaderComponent>

                <TableHeaderComponent
                  ordering={ordering}
                  columnName="_id"
                  onClick={handleSort}
                >
                  ORGANIZATION ID
                </TableHeaderComponent>
                <TableHeaderComponent
                  ordering={ordering}
                  columnName="cycleEndAt"
                  onClick={handleSort}
                  minWidth="290px"
                >
                  CURRENT SUBSCRIPTION PERIOD
                </TableHeaderComponent>
                <TableHeaderComponent
                  ordering={ordering}
                  columnName="ltv"
                  minWidth="182px"
                  onClick={handleSort}
                >
                 PERIOD REVENUE
                </TableHeaderComponent>

                <TableHeaderComponent
                  ordering={ordering}
                  columnName="upcomingRevenue"
                  onClick={handleSort}
                  minWidth="165px"
                >
                  UPCOMING BILL
                </TableHeaderComponent>

                <TableHeaderComponent
                  ordering={ordering}
                  columnName="minMonthlyCharges"
                  onClick={handleSort}
                  minWidth="150px"
                >
                  MONTHLY MIN
                </TableHeaderComponent>

                <TableHeaderComponent
                  ordering={ordering}
                  columnName="createdAt"
                  minWidth="145px"
                  onClick={handleSort}
                >
                  DATE JOINED
                </TableHeaderComponent>

                <TableHeaderComponent
                  ordering={ordering}
                  columnName="phoneNumber"
                  minWidth="182px"
                  onClick={handleSort}
                >
                  PHONE NUMBER
                </TableHeaderComponent>

                <TableHeaderComponent
                  ordering={ordering}
                  columnName="campaignPhoneNumber"
                  minWidth="160px"
                  onClick={handleSort}
                >
                  MMS ENABLED
                </TableHeaderComponent>

                <TableHeaderComponent
                  ordering={ordering}
                  columnName="totalContacts"
                  minWidth="160px"
                  onClick={handleSort}
                >
                  # Active Contacts
                </TableHeaderComponent>

                <TableHeaderComponent
                  ordering={ordering}
                  columnName="totalCampaigns"
                  minWidth="194px"
                  onClick={handleSort}
                >
                  SMS/MMS Campaigns
                </TableHeaderComponent>

                <TableHeaderComponent
                  ordering={ordering}
                  columnName="campaignCreditsUsed"
                  onClick={handleSort}
                >
                  Credits Used
                </TableHeaderComponent>
                {isStagingEnv && (
                  <TableHeaderComponent
                    ordering={ordering}
                    columnName="actions"
                    // onClick={handleSort}
                  >
                    Action
                  </TableHeaderComponent>
                )}
              </TableHeader>
              <>
                {allClients.map((organization) => {
                  return (
                    <TableRow
                      key={organization._id}
                      onClick={() => setOrganizationContextId({
                        variables: {
                          organizationId: organization._id,
                        },
                      })}
                    >
                      <TableRowItem minWidth="210px">
                        {organization.name}
                      </TableRowItem>
                      <TableRowItem minWidth="180px">
                        {organization._id}
                      </TableRowItem>

                      <TableRowItem minWidth="290px">
                        {`${moment(organization.cycleStartAt * 1000).format(
                          'MM/D/YYYY',
                        )} - ${moment(organization.cycleEndAt * 1000).format(
                          'MM/D/YYYY',
                        )}`}
                      </TableRowItem>
                      <TableRowItem minWidth="182px">
                        {Price.CurrencyFormatter(Price.output(organization.ltv, true))}
                      </TableRowItem>
                      <TableRowItem minWidth="165px">
                        {Price.CurrencyFormatter(Price.output(organization.upcomingRevenue, true))}
                      </TableRowItem>
                      <TableRowItem minWidth="150px">
                        {Price.CurrencyFormatter(Price.output(organization.minMonthlyCharges, true))}
                      </TableRowItem>
                      <TableRowItem minWidth="145px">
                        {moment(organization.createdAt * 1000).format(
                          'MMM Do, YYYY',
                        )}
                      </TableRowItem>
                      <TableRowItem minWidth="182px">
                        {organization.phoneNumber ? Phone.formatPhoneNumber(organization.phoneNumber) : ''}
                      </TableRowItem>
                      <TableRowItem minWidth="160px">
                        {organization.mmsEnabled === 'Y'
                        || mmsActivatedIds.includes(organization._id) ? (
                            'Yes'
                          ) : (
                            <>
                              {!!organization.campaignPhoneNumber && (
                                <Button
                                  text="Set Enabled"
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOrgName(organization.name);
                                    setOrganizationId(organization._id);
                                    setMmsEnabledModal(true);
                                  }}
                                  width="100%"
                                  height="25px"
                                  padding="20px 5px"
                                />
                              )}
                            </>
                          )}
                      </TableRowItem>
                      <TableRowItem minWidth="160px">
                        {organization.totalContacts?.toLocaleString() || '0'}
                      </TableRowItem>
                      <TableRowItem minWidth="194px">
                        {organization.totalCampaigns}
                      </TableRowItem>
                      <TableRowItem minWidth="180px">
                        {organization.campaignCreditsUsed?.toLocaleString() || '0'}
                      </TableRowItem>
                      {isStagingEnv && (
                        <TableRowItem minWidth="160px">
                          <Button
                            text="Reset"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOrgName(organization.name);
                              setOrganizationId(organization._id);
                              setResetOrgModal(true);
                            }}
                            width="100%"
                            height="30px"
                            // padding="5px"
                          />
                        </TableRowItem>
                      )}
                    </TableRow>
                  );
                })}
              </>
            </TableContainer>
          )}
        </Section>
        <TemplatesSection />
      </Container>
    </>
  );
}
